import { InjectionToken } from '@angular/core';

export interface ApiBasePaths {
  uaa?: string;
  lpc?: string;
  sfi?: string;
}

/**
 * La configuration OAuth 2 d'une application. **Optionnelle, et c'est l'interrupteur de bascule** :
 * tant qu'elle est absente, la librairie se comporte exactement comme avant — page de login et
 * redirection par `?token=`. La poser sur un environnement suffit à y basculer l'application, sans
 * toucher au code ni aux autres environnements.
 */
export interface OAuthClientConfig {
  /** L'identifiant enregistré côté serveur : `espace-artisan`, `espace-client`, `espace-partenaire`. */
  clientId: string;
  scope: string;
}

export interface AuthLibConfig {
  authUrl: string;
  appType: AuthType;
  apiBasePaths: ApiBasePaths;
  oauth?: OAuthClientConfig;
}

/**
 * Le chemin de rappel, identique dans toutes les applications. C'est une **convention posée côté
 * serveur** : les `redirect-uri` enregistrées sont en correspondance exacte, donc le renommer ici
 * demande de le renommer là-bas.
 */
export const AUTH_CALLBACK_PATH = '/auth/callback';

export const AUTH_LIB_CONFIG = new InjectionToken<AuthLibConfig>(
  'AUTH_LIB_CONFIG'
);

export enum AuthType {
  CLIENT = 'client',
  ARTISAN = 'artisan',
  PARTNER = 'partner',
  EMPLOYE = 'employe',
}

/**
 * Les deux clés de stockage du navigateur, ici et non chacune chez son propriétaire.
 *
 * <strong>Pourquoi elles voyagent ensemble.</strong> `AuthService.eraseStoredToken()` doit effacer
 * les deux — sans quoi la déconnexion laisse derrière elle un jeton de rafraîchissement de 30 jours,
 * et revenir par l'URL reconnecte en silence. Or `AuthService` ne peut pas demander à
 * {@link OAuthSessionService} de le faire : la dépendance va dans l'autre sens. Poser les deux clés
 * dans ce fichier, qui ne dépend de rien, est ce qui permet à l'un d'effacer ce que l'autre écrit
 * sans inverser le graphe d'injection.
 */
export const JWT_STORAGE_KEY = 'JWT_TOKEN';

/**
 * Le jeton de rafraîchissement vit dans le stockage du navigateur : c'est la **route B** de la story
 * O2.6, assumée — un client public ne peut pas garder de secret, et l'alternative (un BFF) a été
 * réservée à `espace-employe`, qui ouvre 210 endpoints internes. Il vaut 30 jours, d'où l'insistance
 * sur son effacement à la déconnexion.
 */
export const REFRESH_TOKEN_STORAGE_KEY = 'RM_REFRESH_TOKEN';

/**
 * Le jeton d'identité, rangé pour une seule raison : la déconnexion OIDC l'exige comme `id_token_hint`,
 * et sans lui `/connect/logout` rend `invalid_request`. Ce n'est pas un identifiant d'accès — aucune API
 * ne l'accepte — mais il porte des informations personnelles, donc il s'efface avec les deux autres.
 *
 * ⚠️ C'est le **dernier** reçu qu'il faut garder : chaque renouvellement en émet un neuf et le serveur
 * oublie le précédent, qui ne serait alors plus reconnu comme indice de déconnexion.
 */
export const ID_TOKEN_STORAGE_KEY = 'RM_ID_TOKEN';

export const TOKEN_QUERY_PARAM_KEY = 'token';

/**
 * Le ticket d'usurpation d'identité — story O2.18.
 *
 * Il remplace le `?token=` sur ce parcours. Ce n'est pas un jeton : il ne vaut que présenté à
 * `/oauth2/authorize`, une seule fois, dans la minute, et avec la session UAA de l'employé à qui il a
 * été remis. Le nom doit rester celui que le serveur lit — `ImpersonationTicketAuthenticationFilter`.
 */
export const IMPERSONATION_TICKET_QUERY_PARAM_KEY = 'impersonation_ticket';
export const ORIGIN_URL_QUERY_PARAM_KEY = 'originUrl';
export const PREFILL_EMAIL_QUERY_PARAM_KEY = 'prefill-email';
